// Copyright (c) Docugami, Inc. All rights reserved.
import { useRef, useCallback } from 'react';

import { addFilesForUpload } from '../../../store/filemanager/actions';
import { AddFilesClickedSchema, AddFolderClickedSchema, TelemetryEvent, UserFilesDroppedSchema } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import React from 'react';

/**
 * Show a file or folder picker UX. You must render the
 * input elements in your component.
 */
export interface FilePicker {
    showFilePicker(): void;
    showFolderPicker(): void;
    inputElements: any[];
}

/**
 * Allows uploading files and folders by dragging and dropping them.
 * The caller should hook up the below callbacks to the component of their choice.
 */
export interface FileDropZone {
    onDrop: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragEnter: (e: React.DragEvent) => void;
    onDataTransfer: (dataTransfer: DataTransfer, rootPathOverride?: string) => void
}

/**
 * Hook for uploading files either via a picker or dragging and dropping.
 */
export function useFileUploader(rootPath?: string, uploadCb?: (items: any) => void, onComplete?: () => void, docsetId?: string): [FilePicker, FileDropZone] {
    const addFilesInputRef = useRef<HTMLInputElement>();
    const addFolderInputRef = useRef<HTMLInputElement>();

    // @ts-ignore
    const fileInputElement = React.createElement('input', { key: "file_upload_element", ref: addFilesInputRef, type: "file", accept: ".pdf, .docx, .doc", multiple: true, onChange: e => setFilesForUpload(e), style: { display: "none" } }, null);
    // @ts-ignore
    const folderInputElement = React.createElement('input', { key: "folder_upload_element", ref: addFolderInputRef, type: "file", multiple: true, webkitdirectory: "", onChange: e => setFilesForUpload(e), style: { display: "none" } }, null);

    const showFilePicker = useCallback(() => {
        Telemetry.trackEvent<AddFilesClickedSchema>({
            eventName: TelemetryEvent.AddFilesClicked
        });
        if (addFilesInputRef.current) {
            addFilesInputRef.current.click();
        }
    }, [addFilesInputRef]);

    const showFolderPicker = useCallback(() => {
        Telemetry.trackEvent<AddFolderClickedSchema>({
            eventName: TelemetryEvent.AddFolderClicked
        });
        if (addFolderInputRef.current) {
            addFolderInputRef.current.click();
        }
    }, [addFolderInputRef]);

    function setFilesForUpload(e: React.ChangeEvent<HTMLInputElement>): void {
        if (e.target.files) {
            var files: File[] = [];
            for (let i = 0; i < e.target.files.length; i++) {
                const file = e.target.files.item(i);
                if (!!file) {
                    files.push(file);
                }
            }

            addFilesForUpload(files, [], rootPath, uploadCb, docsetId);
        }
        onComplete?.();
    }

    async function uploadAllDroppedAsync(items: any[], rootPathOverride?: string): Promise<void> {
        const files: File[] = [];
        const paths: string[] = [];

        for (let i = 0; i < items.length; i++) {
            await buildFileList(items[i], files, paths);
        }

        addFilesForUpload(files, paths, rootPathOverride ?? rootPath, uploadCb, docsetId);
        onComplete?.();
    }

    function readEntriesPromise(directoryReader: any): Promise<any[]> {
        return new Promise((resolve, reject) => {
            directoryReader.readEntries(resolve, reject);
        })
    }

    async function filePromise(item: any): Promise<any> {
        return await new Promise((resolve, reject) => {
            item.file(resolve, reject);
        })
    }

    // Adds every file for upload in item, maintaining directory structures
    async function buildFileList(item: any, files: any[], paths: string[]): Promise<void> {
        if (item.isDirectory) {
            let directoryReader = item.createReader();

            let readEntries = await readEntriesPromise(directoryReader);
            while (readEntries.length > 0) {
                for (let child of readEntries) {
                    await buildFileList(child, files, paths);
                }

                readEntries = await readEntriesPromise(directoryReader);
            }
        } else {
            let file = await filePromise(item);
            files.push(file)
            paths.push(item.fullPath);
        }
    }

    function onDragOver(e: React.DragEvent): void {
        e.preventDefault();
        e.stopPropagation();
    }

    function onDragEnter(e: React.DragEvent): void {
        e.preventDefault();
        e.stopPropagation();
    }

    function onDrop(e: React.DragEvent, rootPathOverride?: string): void {
        e.preventDefault();
        e.stopPropagation();

        if (!e.dataTransfer) {
            return;
        }

        Telemetry.trackEvent<UserFilesDroppedSchema>({
            eventName: TelemetryEvent.UserFilesDropped,
            numOfFiles: e.dataTransfer.files.length
        });

        onDataTransfer(e.dataTransfer, rootPathOverride);
    }

    function onDataTransfer(dataTransfer: DataTransfer, rootPathOverride?: string) {
        const items = [];
        for (let i = 0; i < dataTransfer.items.length; i++) {
            items.push(dataTransfer.items[i].webkitGetAsEntry());
        }

        if (items.length > 0) {
            // fire and forget this dispatching of the dropped items to the upload manager
            uploadAllDroppedAsync(items, rootPathOverride);
        }
    }

    const filePicker = {
        showFilePicker: showFilePicker,
        showFolderPicker: showFolderPicker,
        inputElements: [fileInputElement, folderInputElement]
    };

    const fileDropZone: FileDropZone = {
        onDrop: onDrop,
        onDragEnter: onDragEnter,
        onDragOver: onDragOver,
        onDataTransfer: onDataTransfer
    };

    return [filePicker, fileDropZone];
}
